import React, { useState, useEffect, useRef, useCallback, useLayoutEffect } from 'react';
import ListingCard from '../components/ListingCard';
import ListingRowCard from '../components/ListingRowCard';
import CategoryFilter from '../components/CategoryFilter';
import { getPaginatedListings, getAllCategories, subscribeToStats, getListing } from '../services/listingService';
import { useAuth } from '../contexts/AuthContext';
import { getUserDoc } from '../services/userService';
import { getStatsFromCache, setStatsInCache, getDefaultListingsFromCache, setDefaultListingsInCache, getCategoriesFromCache, setCategoriesInCache } from '../utils/cacheUtils';

const StatsCounter = () => {
  const [stats, setStats] = useState(() => getStatsFromCache());
  const [statsLoading, setStatsLoading] = useState(true);
  const [usingCache, setUsingCache] = useState(true);
  const statsRef = useRef(null);

  useLayoutEffect(() => {
    let isMounted = true;
    
    const unsubscribe = subscribeToStats((newStats) => {
      if (isMounted) {
        setStats(newStats);
        setStatsLoading(false);
        setUsingCache(false);
        setStatsInCache(newStats);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);
  
  return (
    <div ref={statsRef} className="mb-6 inline-flex items-center bg-gray-800 px-4 py-2 rounded-lg border-l-4 border-red-600">
      <div className="flex items-center">
        {statsLoading ? (
          <div className="w-5 h-5 border-2 border-red-500 border-t-transparent rounded-full animate-spin mr-3"></div>
        ) : (
          <svg className="w-5 h-5 text-red-500 mr-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 3a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2V5a2 2 0 00-2-2H5zM5 11a2 2 0 00-2 2v2a2 2 0 002 2h2a2 2 0 002-2v-2a2 2 0 00-2-2H5zM11 5a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V5zM14 11a1 1 0 011 1v1h1a1 1 0 110 2h-1v1a1 1 0 11-2 0v-1h-1a1 1 0 110-2h1v-1a1 1 0 011-1z" />
          </svg>
        )}
        <span className="text-gray-300 text-sm flex items-center">
          <span className="font-bold text-xl text-red-400 counter-number mr-1">
            {stats.totalSitesPublished.toLocaleString()}
          </span>
          sites listed
          {usingCache && !statsLoading && (
            <span className="ml-2 w-3 h-3 border-t border-red-400/50 border-r border-b border-l border-transparent rounded-full animate-spin"></span>
          )}
        </span>
      </div>
    </div>
  );
};

const HomePage = () => {
  const { currentUser } = useAuth();
  
  const [listings, setListings] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All Categories");
  const [categories, setCategories] = useState(() => getCategoriesFromCache());
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [lastVisible, setLastVisible] = useState(null);
  const listingsRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [totalResults, setTotalResults] = useState(0);
  const [viewMode, setViewMode] = useState('card');
  const [usingCache, setUsingCache] = useState(false);

  const pageSize = 15;

  const loadListings = useCallback(async () => {
    setLoading(true);
    const category = activeCategory === "All Categories" ? null : activeCategory;
    const searchQuery = searchTerm.trim() || null;
    
    // Only use cache for default view (no category, no search)
    if (!category && !searchQuery) {
      const cachedData = getDefaultListingsFromCache();
      if (cachedData) {
        const finalListings = [...(cachedData.featuredListings || []), ...cachedData.listings];
        setListings(finalListings);
        setLastVisible(cachedData.lastVisible);
        setHasMore(cachedData.hasMore);
        setTotalResults(cachedData.totalCount);
        setLoading(false);
        setUsingCache(true);
      }
    }

    const result = await getPaginatedListings(category, pageSize, null, searchQuery);
    
    let finalListings = [...result.listings];
    
    // Only add boosted listings when viewing All Categories with no search term
    if (!category && !searchQuery) {
      try {
        const [boostedListing1, boostedListing2] = await Promise.all([
          getListing('gnpSU1wzbaLh0uxnboLi'),
          getListing('i9C96uVsFT4OJZJlCai2')
        ]);
        
        const boostedListings = [];
        if (boostedListing1) boostedListings.push({...boostedListing1, isBoosted: true});
        if (boostedListing2) boostedListings.push({...boostedListing2, isBoosted: true});
        
        // Filter out boosted listings from regular results
        finalListings = result.listings.filter(
          listing => listing.id !== 'gnpSU1wzbaLh0uxnboLi' && listing.id !== 'i9C96uVsFT4OJZJlCai2'
        );
        
        // Cache both regular and featured listings
        setDefaultListingsInCache({
          ...result,
          listings: finalListings
        }, boostedListings);
        
        // Combine boosted listings with regular ones
        finalListings = [...boostedListings, ...finalListings];
      } catch (error) {
        console.error("Error fetching boosted listings:", error);
      }
    }
    
    setListings(finalListings);
    setLastVisible(result.lastVisible);
    setHasMore(result.hasMore);
    setTotalResults(result.totalCount);
    setLoading(false);
    setUsingCache(false);
  }, [activeCategory, pageSize, searchTerm]);

  const loadMoreListings = useCallback(async () => {
    if (!hasMore || loading) return;

    setLoading(true);
    const category = activeCategory === "All Categories" ? null : activeCategory;
    const searchQuery = searchTerm.trim() || null;
    
    const { 
      listings: newListings, 
      lastVisible: newLastVisible, 
      hasMore: newHasMore
    } = await getPaginatedListings(category, pageSize, lastVisible, searchQuery);
    
    setListings(prevListings => [...prevListings, ...newListings]);
    setLastVisible(newLastVisible);
    setHasMore(newHasMore);
    setLoading(false);
  }, [activeCategory, pageSize, lastVisible, hasMore, loading, searchTerm]);

  // Add a ref to track if we're handling initial load
  const isInitialLoadRef = useRef(true);

  // Combine the two separate useEffect hooks into one comprehensive hook that handles both
  // category changes and search term changes
  useEffect(() => {
    const trimmedSearch = searchTerm.trim();
    
    // Handle initial load and category changes
    if (isInitialLoadRef.current) {
      loadListings();
      isInitialLoadRef.current = false;
      return;
    }
    
    // Handle search term changes (with debounce)
    if (trimmedSearch.length === 0 || trimmedSearch.length > 2) {
      setSearchLoading(true);
      const timer = setTimeout(async () => {
        await loadListings();
        setSearchLoading(false);
      }, 500);
      return () => {
        clearTimeout(timer);
        setSearchLoading(false);
      };
    }
  }, [searchTerm, activeCategory, loadListings]);

  useEffect(() => {
    loadCategories();
  }, []);

  const loadCategories = async () => {
    const categories = await getAllCategories();
    setCategories(categories);
    setCategoriesInCache(categories);
  };

  const handleCategoryChange = (category) => {
    setActiveCategory(category);
  };

  // Add effect to fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (currentUser) {
        const userData = await getUserDoc(currentUser.uid);
        if (userData?.firstName) {
          setFirstName(userData.firstName);
        }
      }
    };
    fetchUserData();
  }, [currentUser]);

  // Load saved view preference from localStorage and handle mobile view
  useEffect(() => {
    // Force grid view on mobile
    const isMobile = window.innerWidth < 768;
    
    if (isMobile) {
      setViewMode('card'); // Always use card/grid view on mobile
    } else {
      // Only use saved preference on desktop
      const savedViewMode = localStorage.getItem('preferredViewMode');
      if (savedViewMode === 'list' || savedViewMode === 'card') {
        setViewMode(savedViewMode);
      }
    }
    
    // Add window resize listener to handle orientation changes
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setViewMode('card');
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Toggle between card and list view - prevent switching on mobile
  const toggleViewMode = (mode) => {
    // Don't allow toggling on mobile
    if (window.innerWidth < 768) return;
    
    setViewMode(mode);
    localStorage.setItem('preferredViewMode', mode);
  };

  // Update search form handling
  const handleSearchInput = (e) => {
    e.preventDefault();
    setSearchTerm(e.target.value);
  };

  // Modify the search focus handling to prevent unwanted scrolling
  const handleSearchFocus = () => {
    // Remove scrolling behavior that was causing issues
    // No longer needs to scroll to stats since layout is different
  };

  useEffect(() => {
    if (isInitialLoadRef.current) {
      loadListings();
      isInitialLoadRef.current = false;
    }
  }, [loadListings]);

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Completely Redesigned Hero Section with geometric elements */}
      <section className="relative bg-gray-900 rounded-b-none lg:rounded-br-[10rem] overflow-hidden px-6 pt-10 pb-6 sticky-padding" style={{ minHeight: "30vh" }}>
        {/* Geometric background elements */}
        <div className="absolute inset-0">
          {/* Large diagonal slash */}
          <div className="absolute top-0 right-0 w-full h-full bg-red-800 transform -skew-y-6 origin-top-right opacity-70"></div>
          
          {/* Grid pattern overlay */}
          <div className="absolute inset-0 opacity-20" style={{ 
            backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
            backgroundSize: '20px 20px'
          }}></div>
          
          {/* Geometric shapes */}
          <div className="absolute top-[15%] left-[10%] w-20 h-20 bg-red-600 rounded-lg transform rotate-45 opacity-30"></div>
          <div className="absolute top-[60%] right-[15%] w-32 h-32 border-4 border-gray-300 rounded-full opacity-20"></div>
          <div className="absolute bottom-[10%] left-[30%] w-16 h-16 bg-gray-700 transform rotate-12 opacity-40"></div>
          
          {/* Animated geometric elements */}
          {[...Array(5)].map((_, i) => (
            <div key={i} 
                 className="absolute opacity-20"
                 style={{
                   width: `${Math.random() * 3 + 1}rem`,
                   height: `${Math.random() * 3 + 1}rem`,
                   top: `${Math.random() * 80 + 10}%`,
                   left: `${Math.random() * 80 + 10}%`,
                   backgroundColor: i % 2 === 0 ? '#ef4444' : '#f3f4f6',
                   transform: `rotate(${Math.random() * 45}deg)`,
                   animation: `floatShape ${Math.random() * 8 + 10}s ease-in-out infinite alternate`
                 }}></div>
          ))}
        </div>
        
        {/* Content - Asymmetrical Layout */}
        <div className="container mx-auto relative z-10 px-4 py-8 md:py-12 flex flex-col md:flex-row">
          {/* Left Content Column with Title and Stats */}
          <div className="md:w-7/12 md:pr-8 mb-8 md:mb-0 flex flex-col justify-center">
            {/* Title with industrial design feel */}
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 leading-tight">
              <span className="text-gray-100 block">Discover</span>
              <span className="text-red-500 block mobile-hide">Amazing Apps</span>
              <span className="text-red-500 block desktop-hide">Amazing Sites</span>
              <span className="text-gray-400 block mobile-hide">&amp; Websites</span>
            </h1>
            
            {/* Stats counter with industrial bar indicator */}
            <StatsCounter />
            
            {/* Description text */}
            <p className="text-gray-400 text-lg mb-0 max-w-lg hidden md:block">
              Find top products and services all in one place with Ultimate Directory. Search for the best tools to grow your business.
            </p>
            
            {/* Remove CTA from here as it will be moved to the right column */}
          </div>
          
          {/* Right Column with CTA and Search Box - REORGANIZED LAYOUT */}
          <div className="md:w-5/12 flex flex-col justify-center space-y-4">
            {/* CTA Button - MOVED OUTSIDE SEARCH CONTAINER */}
            <div className="md:block">
              {currentUser ? (
                <div className="flex items-center bg-gray-800 rounded-lg p-3 max-w-full">
                  {/* Removed profile icon div */}
                  <p className="text-gray-300 text-sm">
                    Hey <span className="font-semibold text-white">{firstName}</span>, 
                    <a href="/dashboard" className="ml-1 font-semibold text-red-400 hover:text-red-300 transition-colors">
                      go to your dashboard to manage your listings
                    </a>
                  </p>
                </div>
              ) : (
                <a href="/signup" className="inline-flex items-center justify-center w-full bg-red-700 hover:bg-red-800 text-white font-medium py-3 px-6 rounded-lg shadow-lg transition-colors focus:ring-2 focus:ring-offset-2 focus:ring-red-500 focus:outline-none mobile-button">
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  List Your Website Now
                </a>
              )}
            </div>
            
            {/* Search Container */}
            <div className="bg-gray-800 p-5 rounded-lg shadow-2xl border-t border-gray-700">
              {/* Only show heading on desktop */}
              <h2 className="text-xl font-bold text-white mb-4 hidden md:block">Find What You Need</h2>
              
              {/* Search Form with industrial design */}
              <div>
                <form className="relative" onSubmit={(e) => e.preventDefault()}>
                  <input
                    type={searchLoading ? "text" : "search"}
                    value={searchTerm}
                    onChange={handleSearchInput}
                    onFocus={handleSearchFocus}
                    placeholder="Search for amazing resources..."
                    className="w-full bg-gray-900 text-gray-100 placeholder-gray-500 border-0 border-l-4 border-red-600 rounded-md py-3 pl-4 pr-10 focus:outline-none focus:ring-1 focus:ring-red-500 shadow-inner mobile-search"
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {searchLoading ? (
                      <div className="w-5 h-5 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                    ) : (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  </div>
                </form>
              </div>
              
              {/* Removed the mobile-only description paragraph */}
            </div>
          </div>
        </div>
      </section>

      {/* Add keyframe animations and mobile-specific styles */}
      <style jsx global="true">{`
        @keyframes floatShape {
          0% { transform: translate(0, 0) rotate(0deg); }
          50% { transform: translate(10px, -15px) rotate(10deg); }
          100% { transform: translate(-10px, 15px) rotate(-10deg); }
        }
        
        /* Fix for padding consistency when scrolling */
        section.sticky-padding {
          padding-top: 2.5rem !important;
          padding-bottom: 1.5rem !important;
        }
        
        /* Hide/show elements based on screen size */
        .mobile-hide {
          display: block;
        }
        
        .desktop-hide {
          display: none;
        }
        
        /* Mobile-specific styles */
        @media (max-width: 767px) {
          /* Mobile visibility classes */
          .mobile-hide {
            display: none !important;
          }
          
          .desktop-hide {
            display: block !important;
          }
        
          /* Adjust hero height and padding on mobile - REDUCED */
          section.relative.bg-gray-900 {
            min-height: auto !important;
            padding-bottom: 1rem !important;
            padding-top: 1.5rem !important;
          }
          
          /* Container padding reduction for mobile */
          .container.mx-auto.relative.z-10 {
            padding-top: 0.5rem !important;
            padding-bottom: 0.5rem !important;
          }
          
          /* Increased main heading size on mobile */
          h1.text-4xl {
            font-size: 2.75rem !important;
            line-height: 1.1 !important;
            margin-bottom: 0.5rem !important;
          }
          
          /* Adjust button size on mobile */
          .mobile-button {
            font-size: 0.9rem !important;
            padding-top: 0.5rem !important;
            padding-bottom: 0.5rem !important;
          }
          
          /* Adjust search field on mobile */
          .mobile-search {
            font-size: 0.9rem !important;
            padding-top: 0.5rem !important;
            padding-bottom: 0.5rem !important;
          }
          
          /* Reduce search box padding on mobile - REDUCED FURTHER */
          .bg-gray-800.p-5 {
            padding: 0.5rem !important;
          }
          
          /* Stats styling for mobile */
          .inline-flex.items-center.bg-gray-800 {
            padding: 0.5rem 0.75rem !important;
            margin-bottom: 0.5rem !important;
          }
          
          /* Reduce spacing between items in right column */
          .md\\:w-5\\/12.space-y-4 {
            margin-top: 0 !important;
            gap: 0.5rem !important;
            margin-bottom: 0 !important;
          }
          
          /* Less space between elements */
          .space-y-4 > * + * {
            margin-top: 0.5rem !important;
          }
        }
      `}</style>

      {/* Category Filter - REDUCED BOTTOM PADDING */}
      <section className="container mx-auto mt-2 pt-4 pb-0 px-4">
        {/* <h2 className="text-xl font-bold text-gray-800 mb-4 text-center">Browse by Category</h2> */}
        <div className="category-filter-wrapper">
          <CategoryFilter 
            categories={categories} 
            activeCategory={activeCategory} 
            onCategoryChange={handleCategoryChange} 
          />
        </div>
      </section>

      {/* Listings Section - MORE PROMINENT */}
      <section ref={listingsRef} className="container mx-auto pb-16 px-4">
        {/* Results count and view toggle - Flex container for alignment */}
        <div className="flex flex-wrap items-center justify-between mb-4">
          {/* Results count */}
          <div className="text-gray-600 text-sm">
            {loading ? (
              <span className="inline-flex items-center">
                <div className="w-3 h-3 mr-1 border-t-2 border-b-2 border-gray-500 rounded-full animate-spin"></div>
                Loading results...
              </span>
            ) : (
              // Only show count for search results or category filters
              (searchTerm || activeCategory !== "All Categories") && (
                <span className="flex items-center">
                  {totalResults > 0 ? (
                    <span>
                      <span className="font-bold text-gray-800">{totalResults}</span> listing{totalResults !== 1 ? 's' : ''}
                      {activeCategory !== "All Categories" ? ` in ${activeCategory}` : ''}
                      {searchTerm && ` for "${searchTerm}"`}
                    </span>
                  ) : (
                    <span>No results found</span>
                  )}
                  {usingCache && (
                    <span className="ml-2 w-3 h-3 border-t border-gray-400/50 border-r border-b border-l border-transparent rounded-full animate-spin"></span>
                  )}
                </span>
              )
            )}
          </div>
          
          {/* View toggle buttons - Updated to red/gray theme */}
          {totalResults > 0 && !loading && (
            <div className="inline-flex rounded-md shadow-sm hidden md:flex" role="group">
              <button
                type="button"
                onClick={() => toggleViewMode('card')}
                className={`px-3 py-1.5 text-sm font-medium rounded-l-lg border flex items-center ${
                  viewMode === 'card'
                    ? 'bg-red-700 text-white border-red-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                aria-label="Card view"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                </svg>
                Grid
              </button>
              <button
                type="button"
                onClick={() => toggleViewMode('list')}
                className={`px-3 py-1.5 text-sm font-medium rounded-r-lg border flex items-center ${
                  viewMode === 'list'
                    ? 'bg-red-700 text-white border-red-700'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                aria-label="List view"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                List
              </button>
            </div>
          )}
        </div>
        
        {loading && listings.length === 0 && (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
          </div>
        )}
        
        {/* Conditional rendering based on view mode */}
        {viewMode === 'card' ? (
          // Card/Grid View (original view) - remove wrapper div with border
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {listings.map(listing => (
              <ListingCard key={listing.id} listing={listing} />
            ))}
          </div>
        ) : (
          // List View - using the ListingRowCard component
          <div className="space-y-4">
            {listings.map(listing => (
              <ListingRowCard key={listing.id} listing={listing} />
            ))}
          </div>
        )}
        
        {!loading && listings.length === 0 && (
          <div className="text-center py-16 bg-gray-100 rounded-xl">
            <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            <p className="text-xl text-gray-600">
              {searchTerm 
                ? `No listings found for "${searchTerm}" in ${activeCategory === "All Categories" ? "any category" : `the ${activeCategory} category`}`
                : `No listings found in ${activeCategory === "All Categories" ? "any category" : `the ${activeCategory} category`}`}
            </p>
          </div>
        )}
      </section>

      {/* Load More Button - Updated to red/gray theme */}
      {hasMore && (
        <section className="flex justify-center mb-16">
          <button 
            className="px-8 py-4 bg-gradient-to-r from-red-600 to-gray-700 text-white rounded-lg hover:from-red-700 hover:to-gray-800 transition-colors shadow-lg font-medium transform hover:scale-105 transition-transform duration-300"
            onClick={loadMoreListings}
            disabled={loading}
          >
            {loading ? (
              <span className="inline-flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Loading...
              </span>
            ) : 'Load More Listings'}
          </button>
        </section>
      )}

      {/* Conditional CTA Section - Updated to red/gray theme with COMPACT SIZE */}
      {!currentUser && (
        <section className="relative py-10 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900">
            <div className="absolute inset-0 opacity-10" style={{ 
              backgroundImage: 'linear-gradient(rgba(255, 255, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.1) 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}></div>
          </div>
          <div className="container mx-auto text-center relative z-10 px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-100 mb-4">Boost Your Website's Visibility</h2>
            <p className="text-base md:text-lg text-gray-300 mb-6 max-w-3xl mx-auto">Join thousands of websites in our directory and reach more potential customers. Listing your website takes just a minute.</p>
            <a 
              href="/signup" 
              className="px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 text-white rounded-lg hover:from-red-700 hover:to-red-800 transition-all shadow-lg font-medium transform hover:scale-105 inline-flex items-center text-base"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              List Your Website
            </a>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="container mx-auto px-4 text-center">
          <p className="mb-4">© {new Date().getFullYear()}{' Ultimate Directory. '}
            <a 
              href="https://cloudnext.dev" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="hover:text-blue-300 transition-colors"
            >
              Built by CloudNext
            </a>
          </p>
          {/* Temporarily commented out
          <div className="flex justify-center space-x-5">
            <a href="#" className="text-gray-300 hover:text-white transition-colors">Privacy Policy</a>
            <a href="#" className="text-gray-300 hover:text-white transition-colors">Terms of Service</a>
            <a 
              href="mailto:info@cloudnext.dev?subject=Enquiry/Feedback%20on%20Ultimate%20Directory" 
              className="text-gray-300 hover:text-white transition-colors"
            >
              Contact Us
            </a>
          </div>
          */}
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
